import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Droplets, Users, Wallet } from 'lucide-react';
import { api } from '@/lib/api';
import { formatPriceWithCurrency } from '@/lib/utils';
import { Spinner } from '@/components/Spinner';
import EmptyState from '@/components/EmptyState';

type DateRange = 'week' | 'month' | 'quarter';

interface AnalyticsData {
  revenue: Array<{ date: string; amount: number }>;
  washesPerDay: Array<{ date: string; count: number }>;
  vehicleTypes: { sedan: number; suv: number; large_suv: number };
  totals: {
    washes: number;
    revenue: number;
    avgDaily: number;
  };
}

const COLORS = {
  sedan: '#3b82f6',
  suv: '#f59e0b',
  large_suv: '#8b5cf6',
};

const VEHICLE_LABELS_AR: Record<string, string> = {
  sedan: 'سيدان',
  suv: 'دفع رباعي',
  large_suv: 'دفع رباعي كبير',
};

const rangeLabels: Record<DateRange, string> = {
  week: 'الأسبوع',
  month: 'الشهر',
  quarter: '3 أشهر',
};

export default function AnalyticsPage() {
  const [range, setRange] = useState<DateRange>('month');

  const { data, isLoading } = useQuery<AnalyticsData>({
    queryKey: ['owner', 'analytics', range],
    queryFn: () => api.get(`/owner/analytics?range=${range}`),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const analytics = data || {
    revenue: [],
    washesPerDay: [],
    vehicleTypes: { sedan: 0, suv: 0, large_suv: 0 },
    totals: { washes: 0, revenue: 0, avgDaily: 0 },
  };

  const totalVehicles = analytics.vehicleTypes.sedan + analytics.vehicleTypes.suv + analytics.vehicleTypes.large_suv;

  const pieData = [
    { name: 'سيدان', value: analytics.vehicleTypes.sedan, color: COLORS.sedan },
    { name: 'دفع رباعي', value: analytics.vehicleTypes.suv, color: COLORS.suv },
    { name: 'دفع رباعي كبير', value: analytics.vehicleTypes.large_suv, color: COLORS.large_suv },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">التحليلات</h1>
          <p className="text-gray-500 text-sm mt-1">أداء المغسلة</p>
        </div>
        <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl">
          {(Object.keys(rangeLabels) as DateRange[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                range === r
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              {rangeLabels[r]}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Droplets className="w-6 h-6 text-blue-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{analytics.totals.washes}</p>
          <p className="text-xs text-gray-500">إجمالي الغسلات</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <Wallet className="w-6 h-6 text-green-600 mx-auto mb-1" />
          <p className="text-xl font-bold text-gray-900">{formatPriceWithCurrency(analytics.totals.revenue)}</p>
          <p className="text-xs text-gray-500">الإيرادات</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-card text-center">
          <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-1" />
          <p className="text-2xl font-bold text-gray-900">{analytics.totals.avgDaily}</p>
          <p className="text-xs text-gray-500">معدل يومي</p>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <h2 className="text-base font-bold text-gray-900 mb-4">اتجاه الإيرادات</h2>
        {analytics.revenue.length === 0 ? (
          <EmptyState icon={Wallet} title="لا توجد بيانات" />
        ) : (
          <div className="h-64" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => {
                    const date = new Date(d);
                    return date.toLocaleDateString('ar-IQ', { day: 'numeric', month: 'short' });
                  }}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  formatter={(value: number) => [formatPriceWithCurrency(value), 'الإيرادات']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('ar-IQ')}
                />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Washes Per Day Chart */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <h2 className="text-base font-bold text-gray-900 mb-4">الغسلات اليومية</h2>
        {analytics.washesPerDay.length === 0 ? (
          <EmptyState icon={Droplets} title="لا توجد بيانات" />
        ) : (
          <div className="h-64" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.washesPerDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => {
                    const date = new Date(d);
                    return date.toLocaleDateString('ar-IQ', { day: 'numeric', month: 'short' });
                  }}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value: number) => [value, 'غسلة']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('ar-IQ')}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Vehicle Types */}
      <div className="bg-white rounded-2xl p-5 shadow-card">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-primary-600" />
          <h2 className="text-base font-bold text-gray-900">أنواع السيارات</h2>
        </div>
        {totalVehicles === 0 ? (
          <EmptyState icon={Users} title="لا توجد بيانات" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="h-48" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [`${value} غسلة`, '']} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Breakdown List */}
            <div className="space-y-4">
              {Object.entries(analytics.vehicleTypes).map(([type, count]) => (
                <div key={type}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="flex items-center gap-2 text-gray-700">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[type as keyof typeof COLORS] }}
                      />
                      {VEHICLE_LABELS_AR[type]}
                    </span>
                    <span className="font-bold text-gray-900">{count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${totalVehicles > 0 ? (count / totalVehicles) * 100 : 0}%`,
                        backgroundColor: COLORS[type as keyof typeof COLORS],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
